/**
 * NiV tool — seed the global food catalog.
 *
 * The catalog ships as a JSON export bundled with the Cloud Functions; this
 * panel runs it through the shared normalizer and writes the survivors to the
 * global `foods` collection. Two steps on purpose:
 *
 *   בדיקה יבשה  — validates and shows the report, writes NOTHING
 *   טעינה למאגר — writes, behind a confirm dialog
 *
 * The import is idempotent (document id is a slug of the food name, writes are
 * merges), so re-running it refreshes the catalog instead of duplicating it.
 */

import { useState } from "react";
import {
  CheckCircle,
  Database,
  Eye,
  Warning,
  WarningCircle,
} from "@phosphor-icons/react";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import { useDispatch } from "react-redux";
import { useSeedFoodCatalogMutation } from "../../../../store/services/nivApi";
import { nutritionFoodsApi } from "../../../../store/services/nutritionFoodsApi";
import { useToast } from "../../../../contexts/ToastContext";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";

/** Report rows, in the order an admin reads them. */
const COUNT_ROWS = [
  { key: "input", label: "רשומות בקובץ" },
  { key: "accepted", label: "תקינות" },
  { key: "rejected", label: "נדחו" },
  { key: "duplicatesMerged", label: "כפילויות אוחדו" },
  { key: "recordsWithWarnings", label: "עם אזהרות" },
];

function NivFoodSeedPanel({ isAuthorized }) {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();
  const [report, setReport] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [seedFoodCatalog, { isLoading }] = useSeedFoodCatalogMutation();

  const run = async (dryRun) => {
    try {
      const result = await seedFoodCatalog({ dryRun }).unwrap();
      setReport(result);

      if (dryRun) {
        showSuccess(`בדיקה הסתיימה — ${result.counts.accepted} מזונות תקינים`);
        return;
      }

      showSuccess(`${result.written} מזונות נטענו למאגר`);
      // The food pickers read the catalog through a different API slice, so
      // its cache has to be refreshed explicitly.
      dispatch(
        nutritionFoodsApi.util.invalidateTags([{ type: "Foods", id: "LIST" }])
      );
    } catch (error) {
      console.error("[CK-10045] Error seeding the food catalog:", {
        errorCode: "CK-10045",
        errorMessage: error?.message,
        dryRun,
        timestamp: new Date().toISOString(),
      });
      // The server's message names the reason (e.g. too many rejects).
      showError(error?.message || "טעינת מאגר המזונות נכשלה");
    } finally {
      setConfirmOpen(false);
    }
  };

  if (!isAuthorized) return null;

  const counts = report?.counts;
  const isCommitted = report?.mode === "commit";

  return (
    <div className="nfs-panel">
      <div className="nfs-head">
        <div className="nfs-title">
          <Database size={20} weight="fill" />
          <div>
            <h4>מאגר המזונות הכללי</h4>
            <p>
              טעינת המזונות מקובץ הייצוא. הרצה חוזרת מעדכנת את אותם מזונות ולא
              יוצרת כפילויות.
            </p>
          </div>
        </div>

        <div className="nfs-actions">
          <button
            type="button"
            className="nfs-btn nfs-btn--ghost"
            onClick={() => run(true)}
            disabled={isLoading}
          >
            {isLoading ? (
              <LoaderLoop size="xs" />
            ) : (
              <Eye size={16} />
            )}
            בדיקה יבשה
          </button>
          <button
            type="button"
            className="nfs-btn nfs-btn--primary"
            onClick={() => setConfirmOpen(true)}
            disabled={isLoading}
          >
            <Database size={16} weight="fill" />
            טעינה למאגר
          </button>
        </div>
      </div>

      {counts && (
        <div className={`nfs-report ${isCommitted ? "committed" : ""}`}>
          <div className="nfs-report-head">
            {isCommitted ? (
              <>
                <CheckCircle size={18} weight="fill" />
                <span>נטענו {report.written} מזונות למאגר</span>
              </>
            ) : (
              <>
                <Eye size={18} weight="fill" />
                <span>בדיקה יבשה — לא נכתב דבר</span>
              </>
            )}
          </div>

          <div className="nfs-counts">
            {COUNT_ROWS.map(({ key, label }) => (
              <div key={key} className="nfs-count">
                <span className="nfs-count-value">{counts[key] ?? 0}</span>
                <span className="nfs-count-label">{label}</span>
              </div>
            ))}
          </div>

          {report.rejected?.length > 0 && (
            <details className="nfs-details" open>
              <summary>
                <WarningCircle size={15} weight="fill" />
                רשומות שנדחו ({counts.rejected})
              </summary>
              <ul className="nfs-reject-list">
                {report.rejected.map((entry, index) => (
                  <li key={`${entry.foodName}-${index}`}>
                    <span className="nfs-reject-name">{entry.foodName}</span>
                    <span className="nfs-reject-reason">
                      {entry.errors.join(" · ")}
                    </span>
                  </li>
                ))}
              </ul>
              {report.rejectedTruncated > 0 && (
                <p className="nfs-truncated">
                  ועוד {report.rejectedTruncated} רשומות
                </p>
              )}
            </details>
          )}

          {report.warnings?.length > 0 && (
            <details className="nfs-details">
              <summary>
                <Warning size={15} weight="fill" />
                אזהרות ({counts.recordsWithWarnings})
              </summary>
              <ul className="nfs-reject-list">
                {report.warnings.map((entry, index) => (
                  <li key={`${entry.foodName}-${index}`}>
                    <span className="nfs-reject-name">{entry.foodName}</span>
                    <span className="nfs-reject-reason">
                      {entry.warnings.join(" · ")}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => run(false)}
        title="טעינת מאגר המזונות"
        message={
          counts
            ? `ייכתבו ${counts.accepted} מזונות למאגר הכללי, לכל המרפאות. מזונות קיימים יתעדכנו.`
            : "ייכתבו המזונות מקובץ הייצוא למאגר הכללי, לכל המרפאות. מומלץ להריץ בדיקה יבשה קודם."
        }
        confirmText="טען למאגר"
        cancelText="ביטול"
        type="warning"
      />
    </div>
  );
}

export default NivFoodSeedPanel;
